import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getWorkouts,
  saveWorkout as localSaveWorkout,
  deleteWorkout as localDeleteWorkout,
} from '../utils/fitnessData';
import { fsGetWorkouts, fsSaveWorkout, fsDeleteWorkout } from '../services/firestoreDb';

export function useWorkouts() {
  const { user } = useAuth();
  const uid = user?.id;

  const [workouts, setWorkouts] = useState(() => getWorkouts());

  useEffect(() => {
    if (!uid) return;
    fsGetWorkouts(uid)
      .then((data) => {
        if (data !== null) {
          setWorkouts(data);
        }
      })
      .catch((err) => console.warn('[useWorkouts] Failed to load from Firestore:', err));
  }, [uid]);

  const saveWorkout = useCallback(
    async (workout) => {
      const entry = localSaveWorkout(workout);
      setWorkouts(getWorkouts());
      if (uid) {
        await fsSaveWorkout(uid, entry).catch((err) => console.warn('[useWorkouts] Save failed:', err));
      }
      return entry;
    },
    [uid]
  );

  const deleteWorkout = useCallback(
    async (id) => {
      localDeleteWorkout(id);
      setWorkouts(getWorkouts());
      if (uid) {
        await fsDeleteWorkout(uid, id).catch((err) => console.warn('[useWorkouts] Delete failed:', err));
      }
    },
    [uid]
  );

  const weekWorkouts = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);
    return workouts.filter((w) => new Date(w.date) >= cutoff);
  }, [workouts]);

  const totalDistance = useMemo(
    () => workouts.reduce((sum, w) => sum + (parseFloat(w.distance) || 0), 0),
    [workouts]
  );

  const totalDuration = useMemo(
    () => workouts.reduce((sum, w) => sum + (parseInt(w.duration) || 0), 0),
    [workouts]
  );

  const { currentStreak, longestStreak } = useMemo(() => {
    if (!workouts.length) return { currentStreak: 0, longestStreak: 0 };
    const uniqueDates = [...new Set(workouts.map(w => (w.date || '').slice(0, 10)).filter(Boolean))].sort();
    // Longest streak
    let best = 1, run = 1;
    for (let i = 1; i < uniqueDates.length; i++) {
      const diff = (new Date(uniqueDates[i]) - new Date(uniqueDates[i - 1])) / 86400000;
      if (diff === 1) { run++; if (run > best) best = run; }
      else run = 1;
    }
    // Current streak (from today or yesterday backward)
    const todayStr = new Date().toISOString().slice(0, 10);
    const yestStr  = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    const dateSet  = new Set(uniqueDates);
    let current = 0;
    if (dateSet.has(todayStr) || dateSet.has(yestStr)) {
      let d = new Date(dateSet.has(todayStr) ? todayStr + 'T12:00:00' : yestStr + 'T12:00:00');
      while (dateSet.has(d.toISOString().slice(0, 10))) {
        current++;
        d = new Date(d.getTime() - 86400000);
      }
    }
    return { currentStreak: current, longestStreak: uniqueDates.length === 1 ? 1 : best };
  }, [workouts]);

  const getByType = useCallback(
    (type) => workouts.filter((w) => w.type === type),
    [workouts]
  );

  return { workouts, saveWorkout, deleteWorkout, weekWorkouts, totalDistance, totalDuration, currentStreak, longestStreak, getByType };
}
